import { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Printer, DollarSign, Receipt } from "lucide-react";

function currency(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
}

function ymNow() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function labelForMonth(ym) {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString([], { month: "long", year: "numeric" });
}

export default function StatementPage() {
  const [month, setMonth] = useState(ymNow());
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api
      .get(`/billing/statement?month=${month}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatError(e.response?.data?.detail)));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [month]);

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Monthly Statement" />
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="font-mono-label text-muted-foreground">Billing</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Monthly Statement</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Every closed RO tied to a Decline Drive coupon this month, with your fee auto-calculated.
          </p>
        </div>
        <div className="flex gap-2 no-print">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground font-medium"
            data-testid="statement-month-input"
          />
          <Button
            variant="outline"
            onClick={() => window.print()}
            data-testid="print-statement-btn"
          >
            <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
            Print
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <Card className="p-5 border-border" data-testid="statement-gross">
              <div className="font-mono-label text-muted-foreground">Revenue Recovered</div>
              <div className="font-display text-3xl font-bold tracking-tighter mt-2 text-emerald-600">
                {currency(data.gross_recovered)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {data.closed_ro_count} closed RO{data.closed_ro_count === 1 ? "" : "s"}
              </div>
            </Card>
            <Card className="p-5 border-border" data-testid="statement-fee">
              <div className="font-mono-label text-muted-foreground">Total Due</div>
              <div className="font-display text-3xl font-bold tracking-tighter mt-2 text-[hsl(var(--gold))]">
                {currency(data.fee_due)}
              </div>
              <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                {data.monthly_fee > 0 && (
                  <div>Monthly subscription: {currency(data.monthly_fee)}</div>
                )}
                {data.recurring_fee > 0 && (
                  <div>Recovery fee @ {data.fee_percent}%: {currency(data.recurring_fee)}</div>
                )}
                {data.onboarding_fee > 0 && (
                  <div>Onboarding (one-time): {currency(data.onboarding_fee)}</div>
                )}
                {data.contract_months > 0 && (
                  <div className="pt-1 mt-1 border-t border-border/40">
                    Under {data.contract_months}-month agreement
                  </div>
                )}
              </div>
            </Card>
            <Card className="p-5 border-border">
              <div className="font-mono-label text-muted-foreground">Statement Period</div>
              <div className="font-display text-2xl font-bold tracking-tighter mt-2">
                {labelForMonth(data.month)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Auto-generated</div>
            </Card>
          </div>

          <Card className="p-0 overflow-hidden border-border mb-6">
            <div className="p-5 border-b border-border">
              <div className="font-display text-lg font-bold tracking-tight">Closed ROs</div>
              <div className="text-xs text-muted-foreground mt-1">
                Each row is a customer who booked via a Decline Drive coupon and had the work performed.
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Closed</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>RO #</TableHead>
                  <TableHead className="text-right">Discount</TableHead>
                  <TableHead className="text-right">RO Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.closed_appointments || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-sm text-muted-foreground">
                      <Receipt className="inline w-4 h-4 mr-2" strokeWidth={1.75} />
                      No closed ROs this month yet. Mark appointments closed from the Appointments page to see them here.
                    </TableCell>
                  </TableRow>
                )}
                {(data.closed_appointments || []).map((a) => (
                  <TableRow key={a.id} data-testid={`statement-row-${a.id}`}>
                    <TableCell className="text-xs">
                      {new Date(a.closed_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{a.customer_name}</TableCell>
                    <TableCell className="text-sm">{a.vehicle}</TableCell>
                    <TableCell className="text-sm font-mono">{a.ro_number || "—"}</TableCell>
                    <TableCell className="text-right text-sm">{a.discount}%</TableCell>
                    <TableCell className="text-right font-semibold">
                      {currency(a.ro_total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <div className="text-xs text-muted-foreground border-t border-border pt-4 max-w-3xl grid md:grid-cols-2 gap-6">
            <div>
              <div className="font-mono-label mb-2 text-foreground">Pay by check</div>
              {settings?.remit_to_address ? (
                <div className="text-sm text-foreground/80 leading-relaxed">
                  Make check payable to:<br />
                  <span className="font-semibold text-foreground">{settings.remit_to_name || "Ayers Enterprises"}</span>
                  <br />
                  <span className="whitespace-pre-line">{settings.remit_to_address}</span>
                </div>
              ) : (
                <div>Add your remit-to address in Settings → Remittance to display here.</div>
              )}
            </div>
            <div>
              <div className="font-mono-label mb-2 text-foreground">Pay by ACH</div>
              {settings?.remit_ach_bank ? (
                <div className="text-sm text-foreground/80 leading-relaxed">
                  Bank: <span className="font-semibold text-foreground">{settings.remit_ach_bank}</span><br />
                  Routing: <span className="font-mono">{settings.remit_ach_routing}</span><br />
                  Account: <span className="font-mono">{settings.remit_ach_account}</span>
                </div>
              ) : (
                <div>Add ACH details in Settings → Remittance to display here.</div>
              )}
            </div>
          </div>

          <div className="text-xs text-muted-foreground pt-4 max-w-3xl">
            <div className="font-mono-label mb-1">Fine print</div>
            <div>
              Fee = {data.fee_percent}% of gross recovered service revenue for the period shown. Payable within
              15 days of statement date. Statement generated automatically from RO totals entered against
              closed appointments. {settings?.dealership_name} is billed by {settings?.remit_to_name || "Ayers Enterprises"}.
            </div>
          </div>
        </>
      )}
    </DashboardShell>
  );
}
